import React, { useState, useEffect, useContext } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckIcon,
  DocumentTextIcon,
  TruckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactSelect from 'react-select';
import Swal from 'sweetalert2';
import api from '../../api';
import { AuthContext } from '../../components/AuthContext';
import DetailSection from '../../components/common/DetailSection';
import DetailField from '../../components/common/DetailField';
import StatusBadge from '../../components/common/StatusBadge';
import CopyButton from '../../components/common/CopyButton';
import { InlineLoader, EmptyState } from '../../components/common/StateViews';
import Pagination from '../../components/Pagination';
import Modal from '../../components/Modal';

const GoodsReceived = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [form, setForm] = useState({
    purchaseIndent: null,
    transporterName: '',
    vehicleNumber: '',
    remarks: '',
    receivedItems: [],
    mandatoryItems: [],
    additionalItems: []
  });

  // Data states
  const [goodsReceipts, setGoodsReceipts] = useState([]);
  const [purchaseIndents, setPurchaseIndents] = useState([]);
  const [selectedPurchaseIndentDetails, setSelectedPurchaseIndentDetails] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    receiptDate: null
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, receipt: null });

  // Edit state removed — goods received records are created once.

  useEffect(() => {
    fetchGoodsReceipts();
    fetchPurchaseIndents();
  }, [page, limit, filters]);

  // Keyboard shortcuts: Ctrl+N opens the create form, Escape returns to the list.
  useEffect(() => {
    const handleShortcut = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === 'n') {
          e.preventDefault();
          setTabValue(1);
        }
        return;
      }
      if (e.key === 'Escape' && !viewModal.open) {
        setTabValue(0);
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [viewModal.open]);

  const fetchGoodsReceipts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/goods-received', {
        params: {
          page,
          limit,
          ...filters,
          userId: user.user._id
        }
      });
      setGoodsReceipts(response.data.goodsReceipts || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching goods receipts:', error);
      setGoodsReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseIndents = async () => {
    try {
      const response = await api.get('/goods-received/purchase-indents', {
        params: {
          userId: user.user._id
        }
      });
      setPurchaseIndents(response.data || []);
    } catch (error) {
      console.error('Error fetching purchase indents:', error);
      setPurchaseIndents([]);
    }
  };

  const fetchPurchaseIndentDetails = async (purchaseIndentId) => {
    try {
      setLoading(true);
      const response = await api.get(`/goods-received/purchase-indent/${purchaseIndentId}`, {
        params: {
          userId: user.user._id
        }
      });
      
      setSelectedPurchaseIndentDetails(response.data);
      
      // Combine mandatory and additional items for receiving
      const mandatoryItems = response.data.mandatoryItemsToReceive?.map(item => ({
        ...item,
        itemCategory: 'mandatory',
        receivedQuantity: 0,
        receivedCoverageArea: 0,
        receivedBoxes: 0,
        receivedPieces: 0,
        stockDestination: 'reserved'
      })) || [];

      const additionalItems = response.data.additionalItemsToReceive?.map(item => ({
        ...item,
        itemCategory: 'additional',
        receivedQuantity: 0,
        receivedCoverageArea: 0,
        receivedBoxes: 0,
        receivedPieces: 0,
        stockDestination: 'reserved'
      })) || [];

      setForm(prev => ({
        ...prev,
        mandatoryItems,
        additionalItems,
        receivedItems: [...mandatoryItems, ...additionalItems] // Keep for backward compatibility
      }));
    } catch (error) {
      console.error('Error fetching purchase indent details:', error);
      setSelectedPurchaseIndentDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate form
      if (!form.purchaseIndent) {
        Swal.fire('Error', 'Please select a purchase indent', 'error');
        return;
      }
      
      if (!form.transporterName.trim()) {
        Swal.fire('Error', 'Please enter transporter name', 'error');
        return;
      }
      
      if (!form.vehicleNumber.trim()) {
        Swal.fire('Error', 'Please enter vehicle number', 'error');
        return;
      }
      
      if (!form.receivedItems.some(item =>
        item.pricingMode === 'sqft'
          ? (item.receivedCoverageArea || 0) > 0
          : (item.receivedQuantity || 0) > 0
      )) {
        Swal.fire('Error', 'Please enter at least one received quantity', 'error');
        return;
      }
      
      const submitData = {
        purchaseIndent: form.purchaseIndent._id,
        transporterName: form.transporterName,
        vehicleNumber: form.vehicleNumber,
        remarks: form.remarks,
        receivedItems: form.receivedItems
          .filter(item => item.pricingMode === 'sqft'
            ? (item.receivedCoverageArea || 0) > 0
            : (item.receivedQuantity || 0) > 0)
          .map(item => ({
            itemType: item.itemType,
            item: item.item,
            itemName: item.itemName,
            brandName: item.brandName,
            quantityType: item.quantityType,
            unit: item.unit,
            pricingMode: item.pricingMode || 'piece',
            stockDestination: item.stockDestination || 'reserved',
            coverageArea: item.coverageArea || 0,
            coverageAreaUnit: item.coverageAreaUnit || 'sqft',
            orderedCoverageArea: item.coverageArea || 0,
            receivedCoverageArea: item.receivedCoverageArea || 0,
            purchaseIndentItem: item.purchaseIndentItemId,
            orderedQuantity: item.orderedQuantity,
            receivedQuantity: item.receivedQuantity,
            orderedBoxes: item.orderedBoxes,
            receivedBoxes: item.receivedBoxes,
            orderedPieces: item.orderedPieces,
            receivedPieces: item.receivedPieces,
            proformaInvoiceAllocations: item.proformaInvoiceAllocations
          }))
      };

      await api.post('/goods-received', submitData, {
        params: { userId: user.user._id }
      });

      resetForm();
      setTabValue(0);
      fetchGoodsReceipts();
    } catch (error) {
      console.error('Error saving goods receipt:', error);
      Swal.fire('Error', 'Error saving goods receipt: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      purchaseIndent: null,
      transporterName: '',
      vehicleNumber: '',
      remarks: '',
      receivedItems: [],
      mandatoryItems: [],
      additionalItems: []
    });
    setSelectedPurchaseIndentDetails(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div className="w-full mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Goods Received Management</h1>
        <p className="text-gray-600">Create and manage goods receipts from purchase indents</p>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTabValue(0)}
            className={'py-2 px-1 border-b-2 font-medium text-sm border-blue-500 text-blue-600'}
          >
            Goods Receiveds List
          </button>
          <button
            onClick={() => setTabValue(1)}
            className={'py-2 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
          >
            <PlusIcon className="w-4 h-4 inline mr-1" />
            Create New Receipt
            <span className="ml-2 text-xs opacity-60">Ctrl+N</span>
          </button>
        </nav>
      </div>

      {tabValue === 0 && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Goods Receiveds
              </h3>
            </div>
            {loading ? (
              <InlineLoader message="Loading goods receipts…" />
            ) : goodsReceipts.length === 0 ? (
              <EmptyState
                icon={TruckIcon}
                title="No goods receipts"
                message="Get started by creating a new goods received record."
                action={
                  <button
                    onClick={() => setTabValue(1)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    New Goods Received
                  </button>
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Receipt Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase Indent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transport Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items Count
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {goodsReceipts.map((receipt) => (
                      <tr key={receipt._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {receipt.receiptNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(receipt.receiptDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.purchaseIndent?.indentNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {receipt.purchaseIndent?.brand?.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(receipt.receiptDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.transporterName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {receipt.vehicleNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {receipt.receivedItems?.length || 0} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => setViewModal({ open: true, receipt })}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            limit={limit}
            onLimitChange={setLimit}
            totalItems={total}
          />
        </div>
      )}

      {tabValue === 1 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Create New Goods Received
          </h2>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Purchase Indent *
              </label>
              <ReactSelect
                value={form.purchaseIndent}
                options={purchaseIndents}
                getOptionLabel={(option) => {
                  const completionStatus = option.isCompleted ? 'Completed' : 'In Progress';
                  const itemsToReceive = option.totalBalanceToReceive || 0;
                  return option.indentNumber + ' - ' + option.brand?.name + ' (' + itemsToReceive + ' items to receive) - ' + completionStatus;
                }}
                getOptionValue={(option) => option._id}
                onChange={(selectedOption) => {
                  setForm({ ...form, purchaseIndent: selectedOption });
                  if (selectedOption) {
                    fetchPurchaseIndentDetails(selectedOption._id);
                  } else {
                    setSelectedPurchaseIndentDetails(null);
                    setForm(prev => ({ ...prev, receivedItems: [] }));
                  }
                }}
                placeholder="Select purchase indent with items to receive..."
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {form.purchaseIndent && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Transport Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transporter Name *
                    </label>
                    <input
                      type="text"
                      value={form.transporterName}
                      onChange={(e) => setForm({ ...form, transporterName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter transporter name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle Number *
                    </label>
                    <input
                      type="text"
                      value={form.vehicleNumber}
                      onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter vehicle number"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {form.purchaseIndent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter any remarks..."
                />
              </div>
            )}

            {/* Mandatory Items Section */}
            {form.mandatoryItems.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Mandatory Items to Receive ({form.mandatoryItems.length})
                  </h3>
                  <div className="text-sm text-gray-500">
                    Required items from purchase indent
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Required Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance to Receive
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Received Qty *
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {form.mandatoryItems.map((item, index) => {
                        const isSqft = item.pricingMode === 'sqft';
                        const unitLabel = isSqft ? (item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft') : item.unit;
                        const reqDisplay = isSqft ? `${item.coverageArea} ${unitLabel}` : `${item.requiredQuantity} ${item.unit}`;
                        const coveragePerPiece = (item.pcsPerBox && item.coverageArea && item.requiredQuantity)
                          ? (item.coverageArea / item.requiredQuantity) : 0;
                        const balanceArea = isSqft ? (item.balanceCoverageArea || 0) : item.balanceToReceive;
                        const balDisplay = isSqft ? `${balanceArea} ${unitLabel}` : `${item.balanceToReceive} ${item.unit}`;
                        return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.itemName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.unit} | {item.brandName}
                              {isSqft && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">{unitLabel} pricing</span>}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {reqDisplay}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {balDisplay}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {isSqft ? (
                              <>
                                <input
                                  type="number"
                                  min="0"
                                  max={balanceArea}
                                  step="any"
                                  value={item.receivedCoverageArea || 0}
                                  onChange={(e) => {
                                    const newArea = parseFloat(e.target.value) || 0;
                                    const updatedItems = [...form.mandatoryItems];
                                    updatedItems[index].receivedCoverageArea = newArea;
                                    // derive piece-equivalent for stock
                                    updatedItems[index].receivedQuantity = coveragePerPiece > 0 ? Math.ceil(newArea / coveragePerPiece) : 0;
                                    setForm({
                                      ...form,
                                      mandatoryItems: updatedItems,
                                      receivedItems: [...updatedItems, ...form.additionalItems]
                                    });
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="0"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                  Max: {balanceArea} {unitLabel}
                                </div>
                              </>
                            ) : (
                              <>
                                <input
                                  type="number"
                                  min="0"
                                  max={item.balanceToReceive}
                                  value={item.receivedQuantity || 0}
                                  onChange={(e) => {
                                    const newQuantity = parseFloat(e.target.value) || 0;
                                    const updatedItems = [...form.mandatoryItems];
                                    updatedItems[index].receivedQuantity = newQuantity;
                                    setForm({ 
                                      ...form, 
                                      mandatoryItems: updatedItems,
                                      receivedItems: [...updatedItems, ...form.additionalItems]
                                    });
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="0"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                  Max: {item.balanceToReceive} {item.unit}
                                </div>
                              </>
                            )}
                            <div className="mt-2">
                              <label className="block text-[10px] text-gray-500 mb-0.5">After receipt</label>
                              <select
                                value={item.stockDestination || 'reserved'}
                                onChange={(e) => {
                                  const updatedItems = [...form.mandatoryItems];
                                  updatedItems[index].stockDestination = e.target.value;
                                  setForm({ ...form, mandatoryItems: updatedItems, receivedItems: [...updatedItems, ...form.additionalItems] });
                                }}
                                className="w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="reserved">Reserved</option>
                                <option value="to-be-billed">To be Billed</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Additional Items Section */}
            {form.additionalItems.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Additional Items to Receive ({form.additionalItems.length})
                  </h3>
                  <div className="text-sm text-gray-500">
                    Optional items from purchase indent
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Required Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance to Receive
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Received Qty *
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {form.additionalItems.map((item, index) => {
                        const isSqft = item.pricingMode === 'sqft';
                        const unitLabel = isSqft ? (item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft') : item.unit;
                        const reqDisplay = isSqft ? `${item.coverageArea} ${unitLabel}` : `${item.requiredQuantity} ${item.unit}`;
                        const coveragePerPiece = (item.pcsPerBox && item.coverageArea && item.requiredQuantity)
                          ? (item.coverageArea / item.requiredQuantity) : 0;
                        const balanceArea = isSqft ? (item.balanceCoverageArea || 0) : item.balanceToReceive;
                        const balDisplay = isSqft ? `${balanceArea} ${unitLabel}` : `${item.balanceToReceive} ${item.unit}`;
                        return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.itemName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.unit} | {item.brandName}
                              {isSqft && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">{unitLabel} pricing</span>}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {reqDisplay}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {balDisplay}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {isSqft ? (
                              <>
                                <input
                                  type="number"
                                  min="0"
                                  max={balanceArea}
                                  step="any"
                                  value={item.receivedCoverageArea || 0}
                                  onChange={(e) => {
                                    const newArea = parseFloat(e.target.value) || 0;
                                    const updatedItems = [...form.additionalItems];
                                    updatedItems[index].receivedCoverageArea = newArea;
                                    updatedItems[index].receivedQuantity = coveragePerPiece > 0 ? Math.ceil(newArea / coveragePerPiece) : 0;
                                    setForm({
                                      ...form,
                                      additionalItems: updatedItems,
                                      receivedItems: [...form.mandatoryItems, ...updatedItems]
                                    });
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="0"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                  Max: {balanceArea} {unitLabel}
                                </div>
                              </>
                            ) : (
                              <>
                                <input
                                  type="number"
                                  min="0"
                                  max={item.balanceToReceive}
                                  value={item.receivedQuantity || 0}
                                  onChange={(e) => {
                                    const newQuantity = parseFloat(e.target.value) || 0;
                                    const updatedItems = [...form.additionalItems];
                                    updatedItems[index].receivedQuantity = newQuantity;
                                    setForm({ 
                                      ...form, 
                                      additionalItems: updatedItems,
                                      receivedItems: [...form.mandatoryItems, ...updatedItems]
                                    });
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="0"
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                  Max: {item.balanceToReceive} {item.unit}
                                </div>
                              </>
                            )}
                            <div className="mt-2">
                              <label className="block text-[10px] text-gray-500 mb-0.5">After receipt</label>
                              <select
                                value={item.stockDestination || 'reserved'}
                                onChange={(e) => {
                                  const updatedItems = [...form.additionalItems];
                                  updatedItems[index].stockDestination = e.target.value;
                                  setForm({ ...form, additionalItems: updatedItems, receivedItems: [...form.mandatoryItems, ...updatedItems] });
                                }}
                                className="w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="reserved">Reserved</option>
                                <option value="to-be-billed">To be Billed</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {form.purchaseIndent && form.mandatoryItems.length === 0 && form.additionalItems.length === 0 && selectedPurchaseIndentDetails && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No items to receive</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This purchase indent has no items with remaining balance to receive.
                </p>
              </div>
            )}

            {form.purchaseIndent && (form.mandatoryItems.length > 0 || form.additionalItems.length > 0) && (
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    resetForm();
                    setTabValue(0);
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Create Goods Received'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}


      <Modal
        show={viewModal.open && !!viewModal.receipt}
        onClose={() => setViewModal({ open: false, receipt: null })}
        title={viewModal.receipt?.receiptNumber || 'Goods Received'}
        size="lg"
        headerActions={<CopyButton value={viewModal.receipt?.receiptNumber} title="Copy receipt number" />}
      >
        {viewModal.receipt && (
          <div className="space-y-3">
            <DetailSection title="Receipt" accent="blue">
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                <DetailField label="Status" value={<StatusBadge status={viewModal.receipt.status} />} />
                <DetailField label="Purchase Indent" value={viewModal.receipt.purchaseIndent?.indentNumber} />
                <DetailField label="Brand" value={viewModal.receipt.purchaseIndent?.brand?.name || viewModal.receipt.brandName} />
                <DetailField label="Transporter" value={viewModal.receipt.transporterName} />
                <DetailField label="Vehicle Number" value={viewModal.receipt.vehicleNumber} />
                <DetailField label="Receipt Date" value={new Date(viewModal.receipt.receiptDate).toLocaleString()} />
                <DetailField label="Total Received" value={viewModal.receipt.totalReceivedQuantity} />
                <DetailField label="Total Amount" value={new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(viewModal.receipt.totalAmount || 0)} />
              </dl>
            </DetailSection>

            <DetailSection title={`Items (${viewModal.receipt.receivedItems?.length || 0})`} accent="purple">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2 font-medium">Item</th>
                      <th className="px-3 py-2 font-medium">Brand</th>
                      <th className="px-3 py-2 font-medium text-right">Ordered</th>
                      <th className="px-3 py-2 font-medium text-right">Received</th>
                      <th className="px-3 py-2 font-medium">After Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {viewModal.receipt.receivedItems?.map((item, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2 text-sm text-gray-900">{item.itemName}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">{item.brandName || '-'}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-700">
                          {item.orderedQuantity != null ? `${item.orderedQuantity} ${item.unit || ''}` : '-'}
                        </td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900">{item.receivedQuantity} {item.unit || ''}</td>
                        <td className="px-3 py-2 text-sm">
                          <StatusBadge status={item.stockDestination === 'to-be-billed' ? 'to-be-billed' : 'goods-received'} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DetailSection>

            {viewModal.receipt.remarks && (
              <DetailSection title="Remarks">
                <p className="text-sm text-gray-700">{viewModal.receipt.remarks}</p>
              </DetailSection>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default GoodsReceived;
